import React from "react";
import { Box, Button, Container, Grid, Stack, Typography, Chip, Card } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AutoModeOutlinedIcon from "@mui/icons-material/AutoModeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";

const FEATURES = [
  { icon: <PeopleAltOutlinedIcon />, title: "Employee Management", desc: "Directory, onboarding, offboarding, org chart.", bg: "#EDE8FA", color: "#6C50BF" },
  { icon: <BeachAccessOutlinedIcon />, title: "Leave & Attendance", desc: "Geo-fence check-in, shift config, team calendar.", bg: "#EDF3FA", color: "#4178E5" },
  { icon: <PaymentsOutlinedIcon />, title: "Payroll & Compliance", desc: "PF/ESI/TDS, Form 16, state-wise PT, ECR exports.", bg: "#EDFAF2", color: "#1a7f27" },
  { icon: <AssessmentOutlinedIcon />, title: "Performance & Goals", desc: "Cascading OKRs, 360° reviews, 9-box grid, PIP.", bg: "#FAEDED", color: "#b30010" },
  { icon: <WorkOutlineIcon />, title: "Recruitment ATS", desc: "Public careers page, pipeline, offer letters, referrals.", bg: "#F4EDFA", color: "#8a3fb5" },
  { icon: <AutoModeOutlinedIcon />, title: "Workflows & Analytics", desc: "Custom workflows, DEI, attrition risk, integrations.", bg: "#FFF4D6", color: "#8a6600" },
];

const STATS = [
  { label: "Modules", value: "50+" },
  { label: "States supported (PT)", value: "10" },
  { label: "Statutory forms", value: "6" },
  { label: "Integrations", value: "Slack · Teams · SSO" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          borderBottom: "1px solid",
          borderColor: "divider",
          backdropFilter: "blur(8px)",
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 38, height: 38, borderRadius: 2,
                  background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "white", fontWeight: 800, fontSize: 16,
                }}
              >
                IU
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>InfyU</Typography>
                <Typography sx={{ fontSize: 10, color: "text.secondary", letterSpacing: "0.08em", fontWeight: 600 }}>HRMS PORTAL</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button component={Link} to="/careers" sx={{ color: "text.primary", fontWeight: 600 }}>
                Careers
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                endIcon={<LoginOutlinedIcon />}
                sx={{ fontWeight: 600 }}
              >
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 8, md: 14 },
          background: "linear-gradient(135deg, #EDE8FA 0%, #F4EDFA 50%, #EDF3FA 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute", top: -200, right: -100, width: 600, height: 600,
            background: "radial-gradient(circle, rgba(108,80,191,0.15) 0%, transparent 70%)",
            borderRadius: "50%", pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute", bottom: -150, left: -100, width: 400, height: 400,
            background: "radial-gradient(circle, rgba(65,120,229,0.12) 0%, transparent 70%)",
            borderRadius: "50%", pointerEvents: "none",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                icon={<BoltIcon sx={{ color: "#6C50BF !important" }} />}
                label="The one-stop HRMS for modern teams"
                sx={{
                  bgcolor: "rgba(255,255,255,0.9)", color: "#6C50BF", fontWeight: 600, mb: 3,
                  border: "1px solid rgba(108,80,191,0.2)", fontSize: 12,
                }}
              />
              <Typography sx={{ fontSize: { xs: "2.25rem", md: "3.5rem" }, fontWeight: 800, lineHeight: 1.1, mb: 2, color: "text.primary" }}>
                Manage people,{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #6C50BF 0%, #4178E5 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  pay, and performance
                </Box>{" "}
                in one place.
              </Typography>
              <Typography sx={{ fontSize: { xs: "1rem", md: "1.15rem" }, color: "text.secondary", mb: 4, maxWidth: 580 }}>
                Attendance, leave, payroll, onboarding, performance reviews, recruitment, analytics — every HR workflow your team needs, built with Indian compliance in mind.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ px: 4, py: 1.5, fontSize: 15 }}
                >
                  Sign in to your workspace
                </Button>
                <Button
                  component={Link}
                  to="/careers"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4, py: 1.5, fontSize: 15,
                    bgcolor: "rgba(255,255,255,0.6)",
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": { bgcolor: "white" },
                  }}
                >
                  Explore careers
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: "relative" }}>
                <Card
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 30px 60px rgba(108,80,191,0.2)",
                    transform: { md: "rotate(-2deg)" },
                  }}
                >
                  <Box sx={{ background: "linear-gradient(135deg, #6C50BF 0%, #4178E5 100%)", p: 3, color: "white" }}>
                    <Typography sx={{ fontSize: 11, opacity: 0.9, letterSpacing: "0.1em", fontWeight: 600 }}>
                      GOOD MORNING, ADMIN
                    </Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 700, mt: 0.5 }}>Today's snapshot</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>40</Typography>
                        <Typography sx={{ fontSize: 11, opacity: 0.9 }}>employees</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>159</Typography>
                        <Typography sx={{ fontSize: 11, opacity: 0.9 }}>leaves</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>₹32L</Typography>
                        <Typography sx={{ fontSize: 11, opacity: 0.9 }}>payroll</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ p: 3, bgcolor: "background.paper" }}>
                    {["New Engineer joined", "Payroll for Mar completed", "3 leave approvals pending"].map((t, i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ mb: i < 2 ? 2 : 0 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ["#39c645", "#4178E5", "#f9bc06"][i] }} />
                        <Typography sx={{ fontSize: 13, color: "text.primary" }}>{t}</Typography>
                      </Stack>
                    ))}
                  </Box>
                </Card>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: { xs: 6, md: 10 } }}>
            {STATS.map((s) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 800, color: "primary.main" }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography sx={{ fontSize: { xs: "1.75rem", md: "2.5rem" }, fontWeight: 800, mb: 1.5 }}>
            Everything an HR team needs
          </Typography>
          <Typography sx={{ fontSize: 16, color: "text.secondary", maxWidth: 640, mx: "auto" }}>
            Fifty+ modules built from the ground up for the Indian employment lifecycle. Replace three tools with one.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {FEATURES.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{ height: "100%", p: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.default", transition: "all 0.25s", "&:hover": { borderColor: "primary.main", transform: "translateY(-4px)", boxShadow: "0 20px 40px rgba(108,80,191,0.1)" } }}>
                <Box
                  sx={{
                    width: 52, height: 52, borderRadius: 3,
                    bgcolor: f.bg, color: f.color,
                    display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5,
                  }}
                >
                  {React.cloneElement(f.icon, { sx: { fontSize: 26 } })}
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>{f.title}</Typography>
                <Typography sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.6 }}>{f.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
        <Card
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 5,
            background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 50%, #4178E5 100%)",
            color: "white",
            border: "none",
            position: "relative",
            overflow: "hidden",
            textAlign: "center",
            "&::before": {
              content: '""',
              position: "absolute", top: -100, left: -100, width: 300, height: 300,
              background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
              borderRadius: "50%",
            },
            "&::after": {
              content: '""',
              position: "absolute", bottom: -100, right: -100, width: 300, height: 300,
              background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
              borderRadius: "50%",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: { xs: "1.5rem", md: "2.25rem" }, fontWeight: 800, mb: 1 }}>
              Ready to simplify HR?
            </Typography>
            <Typography sx={{ fontSize: 15, opacity: 0.9, mb: 3, maxWidth: 540, mx: "auto" }}>
              Sign in to your InfyU workspace to manage your organization with modern, compliant HR tooling.
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: "white", color: "primary.main", px: 4, py: 1.5, fontSize: 15, fontWeight: 700,
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              Sign in now
            </Button>
          </Box>
        </Card>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider", py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              © {new Date().getFullYear()} InfyU Labs · HRMS Portal
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button size="small" component={Link} to="/careers" sx={{ fontSize: 12, color: "text.secondary" }}>
                Careers
              </Button>
              <Button size="small" component={Link} to="/login" sx={{ fontSize: 12, color: "text.secondary" }}>
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
